const internals = {};

module.exports = {
    buildClientErrorReport,
    reportClientError
};

internals.MAX_STACK_LENGTH = 12000;
internals.MAX_COMPONENT_STACK_LENGTH = 6000;
internals.MAX_IMPORTANT_FRAMES = 10;
internals.DEDUPLICATION_WINDOW_MS = 10 * 1000;
internals.RATE_LIMIT_WINDOW_MS = 60 * 1000;
internals.MAX_REPORTS_PER_WINDOW = 5;
internals.recentSignatures = new Map();
internals.reportedAt = [];

function buildClientErrorReport({
    error,
    errorInfo,
    source = 'app',
    type = 'client_error',
    recovery = null,
    details = null
} = {}) {

    const normalizedError = internals.normalizeError(error);
    const stack = internals.truncate(internals.redactText(normalizedError.stack));
    const componentStack = internals.truncate(
        String(errorInfo?.componentStack || ''),
        internals.MAX_COMPONENT_STACK_LENGTH
    );

    return {
        type,
        source,
        error: {
            name: internals.truncate(normalizedError.name, 128),
            message: internals.truncate(internals.redactText(normalizedError.message), 4000),
            stack,
            importantFrames: internals.getImportantStackFrames(stack)
        },
        react: componentStack ? {
            componentStack,
            importantComponents: internals.getImportantComponents(componentStack)
        } : null,
        recovery,
        details: internals.sanitizeDetails(details)
    };
}

async function reportClientError(options = {}) {

    const report = buildClientErrorReport(options);
    const now = options.now || Date.now();

    if (!internals.claimReport({ report, now })) {
        return { skipped: true };
    }

    const payload = {
        platform: internals.getPlatform(),
        appVersion: process.env.APP_VERSION || process.env.VERSION || '',
        route: typeof window === 'undefined' ? '' : window.location?.pathname || '',
        userAgent: typeof window === 'undefined' ? '' : window.navigator?.userAgent,
        report
    };

    const isLocal = internals.isLocalEnvironment();
    const isReportingDisabled = process.env.CLIENT_ERROR_REPORTING_ENABLED === 'false';

    if (isLocal || isReportingDisabled) {
        // eslint-disable-next-line no-console
        console.info('[client-error-reporting] Report not sent:', payload);
        return {
            local: isLocal,
            reportingDisabled: isReportingDisabled,
            payload
        };
    }

    const api = internals.getClientErrorApi();

    if (!api?.reportClientError) {
        return { skipped: true };
    }

    return api.reportClientError(payload);
}

internals.normalizeError = (error) => {

    if (error instanceof Error || error?.message || error?.stack) {
        return {
            name: String(error.name || 'Error'),
            message: String(error.message || error || 'Unknown client error'),
            stack: String(error.stack || '')
        };
    }

    return {
        name: 'Error',
        message: typeof error === 'string'
            ? error
            : internals.safeStringify(error) || 'Unknown client error',
        stack: ''
    };
};

internals.claimReport = ({ report, now }) => {

    const signature = [
        report.error.name,
        report.error.message,
        report.error.importantFrames[0] || ''
    ].join(':');
    const lastReportedAt = internals.recentSignatures.get(signature);

    if (typeof lastReportedAt === 'number'
        && now - lastReportedAt < internals.DEDUPLICATION_WINDOW_MS) {
        return false;
    }

    internals.reportedAt = internals.reportedAt.filter(
        (reportedAt) => now - reportedAt < internals.RATE_LIMIT_WINDOW_MS
    );

    if (internals.reportedAt.length >= internals.MAX_REPORTS_PER_WINDOW) {
        return false;
    }

    for (const [previousSignature, reportedAt] of internals.recentSignatures) {
        if (now - reportedAt >= internals.DEDUPLICATION_WINDOW_MS) {
            internals.recentSignatures.delete(previousSignature);
        }
    }

    internals.recentSignatures.set(signature, now);
    internals.reportedAt.push(now);

    return true;
};

internals.getImportantStackFrames = (stack) => {

    const frames = String(stack || '')
        .split('\n')
        .map((frame) => frame.trim())
        .filter((frame) => /^(?:at\s|\S+@)/.test(frame));
    const applicationFrames = frames.filter((frame) => !internals.isFrameworkFrame(frame));
    const selectedFrames = applicationFrames.length ? applicationFrames : frames;

    return selectedFrames
        .slice(0, internals.MAX_IMPORTANT_FRAMES)
        .map((frame) => internals.truncate(frame, 2000));
};

internals.isFrameworkFrame = (frame) => {

    const isFrameworkSource = /(?:node_modules|react-dom|react\.development|scheduler|webpack\/runtime)/i.test(frame);
    const isFrameworkFunction = /(?:renderWithHooks|updateFunctionComponent|beginWork|performUnitOfWork|workLoop|renderRoot|commitRoot|invokeGuardedCallback)/i.test(frame);

    return isFrameworkSource || isFrameworkFunction;
};

internals.getImportantComponents = (componentStack) => {

    return String(componentStack || '')
        .split('\n')
        .map((frame) => frame.trim())
        .filter(Boolean)
        .slice(0, internals.MAX_IMPORTANT_FRAMES)
        .map((frame) => internals.truncate(frame, 1000));
};

internals.sanitizeDetails = (details) => {

    if (!details || typeof details !== 'object') {
        return null;
    }

    return {
        filename: internals.truncate(internals.redactText(details.filename), 2048),
        lineno: Number.isInteger(details.lineno) ? details.lineno : null,
        colno: Number.isInteger(details.colno) ? details.colno : null
    };
};

internals.truncate = (value, maxLength = internals.MAX_STACK_LENGTH) => {

    return String(value || '').slice(0, maxLength);
};

internals.redactText = (value) => {

    return String(value || '')
        .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [redacted]')
        .replace(/(token|password|secret|authorization|credential|session|jwt|firebase)=([^\s&#]+)/gi, '$1=[redacted]');
};

internals.safeStringify = (value) => {

    try {
        return JSON.stringify(value);
    }
    catch (err) {
        return String(value);
    }
};

internals.getClientErrorApi = () => {

    try {
        return require('app-context').context.api.nearpeer.supportDebug;
    }
    catch (err) {
        return null;
    }
};

internals.getPlatform = () => {

    if (typeof window !== 'undefined' && window.cordova) {
        return 'cordova';
    }

    return 'web';
};

internals.isLocalEnvironment = () => {

    if (process.env.NODE_ENV === 'test') {
        return false;
    }

    if (process.env.NODE_ENV === 'development') {
        return true;
    }

    if (typeof window === 'undefined' || window.cordova) {
        return false;
    }

    const apiHost = String(process.env.API_HOST || '');
    const browserHost = String(window.location?.hostname || '');
    const isLocalHost = (host) => /^(?:https?:\/\/)?(?:localhost|127\.0\.0\.1|\[?::1\]?)(?::|\/|$)/i.test(host);

    return isLocalHost(apiHost) || isLocalHost(browserHost);
};

if (process.env.NODE_ENV === 'test') {
    internals.reset = () => {

        internals.recentSignatures.clear();
        internals.reportedAt = [];
    };

    module.exports._internals = internals;
}
